import type { PrintViewModel } from '@kop/print';

export type MeetingAttachment = Readonly<{ manifestId: string; sha256: string; fileName?: string }>;
export type MeetingKind = 'daily-safety' | 'committee';
export type MeetingCadence = 'weekly' | 'monthly' | null;
export type MeetingFollowUp = Readonly<{
  id: string;
  content: string;
  owner: string | null;
  dueDate: string | null;
  status: 'open' | 'done';
}>;
export type MeetingMinutes = Readonly<{
  id: string;
  date: string;
  time: string;
  kind: MeetingKind;
  cadence: MeetingCadence;
  type: string;
  place: string;
  contractorId: string | null;
  contractorEngagementId: string | null;
  contractorName: string | null;
  title: string;
  attendees: readonly string[];
  agenda: string;
  discussion: string;
  decisions: string;
  followUps: readonly MeetingFollowUp[];
  attachments: readonly MeetingAttachment[];
  status: 'draft' | 'submitted' | 'returned' | 'completed';
  version: number;
  approvalRequestId: string | null;
  printEvidence: Readonly<{ completionId: string; contentHash: string }> | null;
  allowedActions: readonly ('edit' | 'complete' | 'review' | 'archive' | 'print')[];
}>;

export type MeetingMinutesDraft = Omit<
  MeetingMinutes,
  'status' | 'version' | 'approvalRequestId' | 'printEvidence' | 'allowedActions'
>;

export type MeetingMinutesPage = Readonly<{
  records: readonly MeetingMinutes[];
  totalCount: number;
  nextCursor: string | null;
  allowedActions: readonly 'create'[];
  contractorOptions: readonly Readonly<{ id: string; name: string; engagementId: string }>[];
}>;

export const meetingKindOptions = [
  { value: 'daily-safety', label: '일일안전회의' },
  { value: 'committee', label: '안전보건협의체' },
] as const;
export const meetingCadenceOptions = [
  { value: 'weekly', label: '주간' },
  { value: 'monthly', label: '월간' },
] as const;

const text = (value: unknown) => String(value ?? '');
const records = (value: unknown) => (Array.isArray(value) ? value : []);

export function normalizeMeetingClassification(value: Readonly<{
  type?: unknown;
  kind?: unknown;
  cadence?: unknown;
}>): {
  kind: MeetingKind;
  cadence: MeetingCadence;
  type: string;
} {
  const legacyType = text(value.type).trim();
  const kind = value.kind === 'daily-safety' || value.kind === 'committee'
    ? value.kind
    : legacyType === '일일안전회의'
      ? 'daily-safety'
      : legacyType === '안전보건협의체-주간' || legacyType === '안전보건협의체-월간'
        ? 'committee'
        : null;
  if (!kind) throw new Error('지원하는 회의 종류를 선택하세요.');
  const cadence = kind === 'daily-safety'
    ? null
    : value.cadence === 'weekly' || value.cadence === 'monthly'
      ? value.cadence
      : legacyType === '안전보건협의체-주간'
        ? 'weekly'
        : legacyType === '안전보건협의체-월간'
          ? 'monthly'
          : null;
  if (kind === 'committee' && cadence === null)
    throw new Error('협의체 주기를 선택하세요.');
  return {
    kind,
    cadence,
    type: kind === 'daily-safety'
      ? '일일안전회의'
      : `안전보건협의체-${cadence === 'weekly' ? '주간' : '월간'}`,
  };
}

export function decodeMeetingMinutesPage(value: unknown): MeetingMinutesPage {
  if (!value || typeof value !== 'object') throw new Error('회의록 목록을 확인할 수 없습니다.');
  const source = value as Record<string, unknown>;
  if (source.complete !== true) throw new Error('회의록 전체 범위를 확인할 수 없습니다.');
  const decoded = records(source.records).map((item) => {
    if (!item || typeof item !== 'object') throw new Error('회의록 자료 형식이 올바르지 않습니다.');
    const row = item as Record<string, unknown>;
    const status = ['draft', 'submitted', 'returned', 'completed'].includes(String(row.status))
      ? row.status as MeetingMinutes['status']
      : null;
    const version = Number(row.version);
    if (!text(row.meetingId ?? row.id) || !status || !Number.isInteger(version) || version < 1)
      throw new Error('회의록의 식별 정보가 올바르지 않습니다.');
    const classification = normalizeMeetingClassification(row);
    return {
      id: text(row.meetingId ?? row.id),
      date: text(row.date),
      time: text(row.time),
      ...classification,
      place: text(row.place),
      contractorId: typeof row.contractorId === 'string' ? row.contractorId : null,
      contractorEngagementId:
        typeof row.contractorEngagementId === 'string' ? row.contractorEngagementId : null,
      contractorName: typeof row.contractorName === 'string' ? row.contractorName : null,
      title: text(row.title),
      attendees: records(row.attendees).map(text).filter(Boolean),
      agenda: text(row.agenda),
      discussion: text(row.discussion),
      decisions: text(row.decisions),
      followUps: records(row.followUps).flatMap((entry) => {
        if (!entry || typeof entry !== 'object') return [];
        const action = entry as Record<string, unknown>;
        return [
          {
            id: text(action.id),
            content: text(action.content),
            owner: typeof action.owner === 'string' && action.owner.trim() ? action.owner.trim() : null,
            dueDate: typeof action.dueDate === 'string' && action.dueDate.trim() ? action.dueDate : null,
            status: action.status === 'done' ? ('done' as const) : ('open' as const),
          },
        ];
      }),
      attachments: records(row.attachments).flatMap((entry) => {
        if (!entry || typeof entry !== 'object') return [];
        const attachment = entry as Record<string, unknown>;
        const fileName = text(attachment.fileName).trim();
        return [{
          manifestId: text(attachment.manifestId),
          sha256: text(attachment.sha256),
          ...(fileName ? { fileName } : {}),
        }];
      }),
      status,
      version,
      approvalRequestId: typeof row.approvalRequestId === 'string' ? row.approvalRequestId : null,
      printEvidence:
        row.printEvidence && typeof row.printEvidence === 'object'
          ? {
              completionId: text((row.printEvidence as Record<string, unknown>).completionId),
              contentHash: text((row.printEvidence as Record<string, unknown>).contentHash),
            }
          : null,
      allowedActions: records(row.allowedActions).filter(
        (action): action is 'edit' | 'complete' | 'review' | 'archive' | 'print' =>
          ['edit', 'complete', 'review', 'archive', 'print'].includes(String(action)),
      ),
    } satisfies MeetingMinutes;
  });
  const totalCount = Number(source.totalCount);
  if (!Number.isInteger(totalCount) || totalCount < decoded.length)
    throw new Error('회의록 집계가 올바르지 않습니다.');
  return {
    records: decoded,
    totalCount,
    nextCursor: typeof source.nextCursor === 'string' ? source.nextCursor : null,
    allowedActions: records(source.allowedActions).includes('create') ? ['create'] : [],
    contractorOptions: records(source.contractorOptions).flatMap((entry) => {
      if (!entry || typeof entry !== 'object') return [];
      const option = entry as Record<string, unknown>;
      const id = text(option.id);
      const name = text(option.name);
      const engagementId = text(option.engagementId);
      return id && name && engagementId ? [{ id, name, engagementId }] : [];
    }),
  };
}

export function validateMeetingDraft(draft: MeetingMinutesDraft) {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(draft.date) || !/^([01]\d|2[0-3]):[0-5]\d$/u.test(draft.time))
    throw new Error('회의 일시를 입력하세요.');
  if (
    ![draft.type, draft.place, draft.title, draft.agenda, draft.discussion].every(
      (value) => value.trim(),
    )
  )
    throw new Error('회의 종류·장소·제목·공유·논의 내용을 입력하세요.');
  if (!draft.attendees.some((value) => value.trim())) throw new Error('참석자를 입력하세요.');
  normalizeMeetingClassification(draft);
  for (const action of draft.followUps)
    if (
      !action.content.trim() ||
      (action.dueDate != null && !/^\d{4}-\d{2}-\d{2}$/u.test(action.dueDate))
    )
      throw new Error('후속조치 내용과 올바른 기한을 입력하세요.');
}

export function buildMeetingMinutesPrintModel(
  meeting: MeetingMinutes,
  actor: string,
  evidenceAssets: PrintViewModel['assets'] = [],
): PrintViewModel {
  return {
    schemaVersion: '1.0',
    identity: {
      documentType: '회의록',
      documentId: meeting.id,
      revisionId: `${meeting.id}-v${meeting.version}`,
      versionLabel: `v${meeting.version}`,
    },
    title: meeting.title,
    locale: 'ko-KR',
    purpose: 'internal-review',
    pageProfileId: 'a4-portrait-evidence',
    generatedAt: new Date().toISOString(),
    generatedBy: { actorId: actor, displayName: actor, organizationName: meeting.contractorName ?? 'KOP' },
    target: {
      capturedAt: new Date().toISOString(),
      recordIds: [meeting.id],
      declaredRecordCount: 1,
      filterSummary: [{ key: 'place', label: '회의 장소', value: meeting.place }],
      sourceVersion: meeting.printEvidence
        ? `meetingRecordCompletions:${meeting.printEvidence.completionId}:${meeting.printEvidence.contentHash}`
        : `meetingRecords-v${meeting.version}`,
    },
    privacy: { mode: 'minimum', redactedFieldKeys: [] },
    assets: evidenceAssets,
    sections: [
      {
        kind: 'fields',
        id: 'meeting',
        title: '회의 정보',
        fields: [
          { key: 'date', label: '일시', value: `${meeting.date} ${meeting.time}`, required: true },
          { key: 'type', label: '회의 종류', value: meeting.type },
          { key: 'place', label: '장소', value: meeting.place },
          { key: 'contractor', label: '업체', value: meeting.contractorName ?? '전체' },
          { key: 'attendees', label: '참석자', value: meeting.attendees.join(', ') },
        ],
      },
      ...(evidenceAssets.length
        ? [{
            kind: 'photos' as const,
            id: 'meeting-attachments',
            title: '첨부 증거',
            columns: 2 as const,
            items: evidenceAssets.map((asset, index) => ({
              id: `meeting-attachment-${index + 1}`,
              assetId: asset.id,
              label: asset.label || `첨부 ${index + 1}`,
              evidenceRole: 'other' as const,
            })),
          }]
        : []),
      { kind: 'narrative', id: 'agenda', title: '안건', body: meeting.agenda },
      { kind: 'narrative', id: 'discussion', title: '논의 내용', body: meeting.discussion },
      { kind: 'narrative', id: 'decisions', title: '결정사항', body: meeting.decisions },
      {
        kind: 'narrative',
        id: 'follow-ups',
        title: '후속조치',
        body:
          meeting.followUps
          .map((item) => [item.content, item.owner, item.dueDate].filter(Boolean).join(' · '))
            .join('\n') || '없음',
      },
    ],
    warnings: meeting.status === 'draft'
      ? ['작성 중인 회의록입니다.']
      : meeting.printEvidence === null
        ? ['완료 스냅샷 증거가 없는 이전 회의록입니다.']
        : [],
  };
}
